const { activityForSettings, isCompleted } = require('./activity_completion');

// One shared history snapshot for every Explore drilldown. Navigation only
// decorates rows; it never fetches or changes their launch/navigation data.
class ActivityCompletionList {
  constructor(container, getHistories) {
    this.container = container;
    this.getHistories = getHistories;
    this.document = container.ownerDocument;
    this.window = this.document.defaultView;
    this.histories = [];
    this.requestId = 0;
    try {
      this.config = JSON.parse(container.dataset.activityCompletion || 'null');
    } catch (_) {
      this.config = null;
    }
    if (!this.config) return;

    this.language = this.readLanguage();
    this.onVisibility = () => { if (this.document.visibilityState === 'visible') this.refresh(); };
    this.onLanguage = event => { if (event.target.id === 'chat_form_language') this.refresh(); };
    this.onPageShow = event => { if (event.persisted) this.refresh(); };
    this.onBeforeCache = () => this.destroy();
    this.document.addEventListener('visibilitychange', this.onVisibility);
    this.document.addEventListener('change', this.onLanguage);
    this.document.addEventListener('turbolinks:before-cache', this.onBeforeCache);
    this.window.addEventListener('pageshow', this.onPageShow);
    this.refresh();
  }

  readLanguage() {
    return this.document.getElementById('chat_form_language')?.value || this.config.language;
  }

  refresh() {
    if (!this.config || this.destroyed) return Promise.resolve();
    const language = this.readLanguage();
    if (this.pending && language === this.language) return this.pending;
    this.language = language;
    const requestId = ++this.requestId;
    this.histories = [];
    this.render();
    // Defer the request so even a synchronous client failure cannot block lists.
    this.pending = Promise.resolve().then(() => language ? this.getHistories(language) : {})
      .then(data => {
        if (requestId !== this.requestId || this.destroyed) return;
        this.histories = Array.isArray(data?.activity_histories) ? data.activity_histories : [];
      })
      .catch(() => { /* Completion is optional; leave the lists usable without marks. */ })
      .finally(() => {
        if (requestId !== this.requestId || this.destroyed) return;
        this.pending = null;
        this.render();
      });
    return this.pending;
  }

  render() {
    if (!this.config || this.destroyed) return;
    this.clearMarks();
    this.container.querySelectorAll('.js-sub-category-btn, .js-sub-chat-option-btn').forEach(button => {
      const finalTopic = button.classList.contains('js-sub-chat-option-btn');
      let hasChildren = false;
      if (!finalTopic && button.dataset.topics) {
        try { hasChildren = JSON.parse(button.dataset.topics) != null; } catch (_) { hasChildren = true; }
      }
      const activity = activityForSettings({
        topic_category: button.dataset.topicCategory,
        topic: finalTopic ? button.dataset.topic : button.dataset.subtopicCategory,
        subtopic_category: finalTopic ? button.dataset.subtopicCategory : ''
      }, hasChildren);
      if (!isCompleted(activity, this.histories, this.language, this.config.lesson_aliases)) return;

      const mark = this.document.createElement('span');
      mark.className = 'chat-activity-completion js-activity-completion-mark';
      mark.setAttribute('role', 'img');
      mark.setAttribute('aria-label', this.config.completed_label);
      mark.title = this.config.completed_label;
      const icon = this.document.createElement('i');
      icon.className = 'fa fa-check-circle';
      icon.setAttribute('aria-hidden', 'true');
      mark.appendChild(icon);
      button.classList.add('chat-topic-button--completed');
      button.appendChild(mark);
    });
  }

  clearMarks() {
    this.container.querySelectorAll('.js-activity-completion-mark').forEach(mark => mark.remove());
    this.container.querySelectorAll('.chat-topic-button--completed').forEach(button => button.classList.remove('chat-topic-button--completed'));
  }

  destroy() {
    if (!this.config || this.destroyed) return;
    this.destroyed = true;
    ++this.requestId;
    this.histories = [];
    this.clearMarks();
    this.document.removeEventListener('visibilitychange', this.onVisibility);
    this.document.removeEventListener('change', this.onLanguage);
    this.document.removeEventListener('turbolinks:before-cache', this.onBeforeCache);
    this.window.removeEventListener('pageshow', this.onPageShow);
  }
}

module.exports = ActivityCompletionList;
